/**
 * Agencies as the app knows them.
 *
 * There is no agency table: `talents.Agentur` is a free-text name and every agency field
 * (`agency_*`, AP, the three flags) is stored on each talent that belongs to the agency.
 * "An agency" is therefore the group of talents sharing a name, and its master data is what
 * those talents carry. This module is the one place that derives the list, so Agency
 * Management and the talent edit form agree on it.
 *
 * Why the merge below matters (bug reported 2026-10-20): Agency Management used to seed the
 * agency form from the FIRST talent with that name. A talent that had only the name typed in
 * (no VAT, bank, address) could become that first talent, the form showed blanks, and saving
 * wrote the blanks to every talent of the agency. The master data is now taken from the most
 * complete member, field by field.
 */

export const AGENCY_FIELDS = [
  'agency_vat_status',
  'agency_tax_id',
  'agency_vat_id',
  'agency_bank_account',
  'agency_email',
  'agency_address',
  'AP',
  'is_agency',
  'agency_bill_separately',
  'agency_own_account',
];

const TEXT_FIELDS = ['agency_vat_status', 'agency_tax_id', 'agency_vat_id', 'agency_bank_account', 'agency_email', 'agency_address', 'AP'];
const FLAG_FIELDS = ['is_agency', 'agency_bill_separately', 'agency_own_account'];

const asFlag = (v) => v === true || v === 1 || v === '1';
const asText = (v) => (v == null ? '' : String(v).trim());

/** "Agentur Stammhaus " and "agentur   stammhaus" are the same agency. */
export const normalizeAgencyName = (name) => asText(name).replace(/\s+/g, ' ').toLowerCase();

export const hasAgencyName = (talent) => normalizeAgencyName(talent?.Agentur ?? talent?.agentur) !== '';

/** How many agency fields a talent actually has filled — used to pick the master record. */
const completeness = (t) => TEXT_FIELDS.filter((k) => asText(t?.[k]) !== '').length;

/**
 * Group talents into agencies. Each agency: { key, name, talents, ...AGENCY_FIELDS }.
 * - key: normalised name (grouping key)
 * - name: the spelling most members use (ties → the first seen)
 * - fields: from the most complete member; any field still empty is filled from the first
 *   member that has it; flags come from the most complete member too.
 */
export function buildAgencies(talents) {
  const groups = new Map();
  (talents || []).forEach((t) => {
    const key = normalizeAgencyName(t?.Agentur ?? t?.agentur);
    if (!key) return;
    if (!groups.has(key)) groups.set(key, { key, members: [], spellings: new Map() });
    const g = groups.get(key);
    g.members.push(t);
    const raw = asText(t.Agentur ?? t.agentur);
    g.spellings.set(raw, (g.spellings.get(raw) || 0) + 1);
  });

  return [...groups.values()]
    .map((g) => {
      const master = [...g.members].sort((a, b) => completeness(b) - completeness(a))[0];
      const agency = { key: g.key, talents: g.members };
      agency.name = [...g.spellings.entries()].sort((a, b) => b[1] - a[1])[0][0];
      TEXT_FIELDS.forEach((k) => {
        const fromMaster = asText(master?.[k]);
        agency[k] = fromMaster || asText((g.members.find((m) => asText(m?.[k]) !== '') || {})[k]);
      });
      FLAG_FIELDS.forEach((k) => { agency[k] = asFlag(master?.[k]); });
      return agency;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Find the agency a talent belongs to in a list from buildAgencies(). */
export const findAgencyOf = (agencies, talent) => {
  const key = normalizeAgencyName(talent?.Agentur ?? talent?.agentur);
  return key ? (agencies || []).find((a) => a.key === key) || null : null;
};

/**
 * The PATCH payload that puts a talent under `agency`: the agency's exact spelling plus every
 * agency field, so the new member carries the same data as the others.
 */
export function agencyPayload(agency) {
  const payload = { Agentur: agency.name };
  TEXT_FIELDS.forEach((k) => { payload[k] = agency[k] || ''; });
  payload.is_agency = true; // a talent under an agency is billed via that agency
  payload.agency_bill_separately = !!agency.agency_bill_separately;
  payload.agency_own_account = !!agency.agency_own_account;
  return payload;
}

/** The PATCH payload that takes a talent out of its agency (name and all agency fields). */
export function clearedAgencyPayload() {
  const payload = { Agentur: '' };
  TEXT_FIELDS.forEach((k) => { payload[k] = ''; });
  FLAG_FIELDS.forEach((k) => { payload[k] = false; });
  return payload;
}
